import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { store } from '@/storage/sqliteStore';
import { authClient, cloud, device } from '@/backend';
import { appPath } from '@/lib/appUrl';
import { isAtLeast } from '@/lib/roles';

import { safeLocalStorage } from '@/lib/browserStorage';
const AuthContext = createContext();

// Local auth is the source of truth for whether an already-authenticated
// installation may open the app. Supabase is the cloud identity/sync layer.
const LOCAL_AUTH_KEY = 'drilllog_offline_auth_user';
const EXPLICIT_LOGOUT_KEY = 'drilllog_explicitly_logged_out';
const USER_ID_KEY = 'drilllog_user_id';
const AUTH_STATE_ID = 'active';

function readLocalAuth() {
  try {
    const raw = safeLocalStorage.getItem(LOCAL_AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocalAuth(user) {
  if (!user?.id) return;
  safeLocalStorage.setItem(LOCAL_AUTH_KEY, JSON.stringify({
    id: user.id,
    auth_user_id: user.auth_user_id || user.id,
    email: user.email || null,
    name: user.name || 'User',
    role: user.role || 'rig',
  }));
  safeLocalStorage.setItem(USER_ID_KEY, user.id);
  safeLocalStorage.removeItem(EXPLICIT_LOGOUT_KEY);
}

async function persistAuthState(user) {
  if (!user?.id) return;
  try { await store('AuthState').put({ id: AUTH_STATE_ID, user }); } catch (e) { console.warn('[DrillLogPro] auth state persistence skipped:', e?.message || e); }
}

async function clearPersistedAuthState() {
  try { await store('AuthState').delete(AUTH_STATE_ID); } catch (e) { console.warn('[DrillLogPro] auth state clear skipped:', e?.message || e); }
}

function clearLocalAuth() {
  safeLocalStorage.removeItem(LOCAL_AUTH_KEY);
  safeLocalStorage.removeItem(USER_ID_KEY);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [appPublicSettings] = useState({ id: 'drilllogpro-cloud', public_settings: { standalone: true, cloudAuth: true } });
  const [viewAsRole, setViewAsRoleState] = useState(() => safeLocalStorage.getItem('drilllog-view-as-role') || null);

  const setViewAsRole = (role) => {
    // Only a real admin may use the role preview. The preview must never
    // elevate a non-admin user's effective permissions.
    if (!isAtLeast(user?.role, 'admin')) {
      setViewAsRoleState(null);
      safeLocalStorage.removeItem('drilllog-view-as-role');
      return;
    }
    setViewAsRoleState(role);
    if (role) safeLocalStorage.setItem('drilllog-view-as-role', role);
    else safeLocalStorage.removeItem('drilllog-view-as-role');
  };

  const effectiveViewAsRole = viewAsRole && isAtLeast(user?.role, viewAsRole) ? viewAsRole : null;
  const effectiveRole = isAtLeast(user?.role, 'admin') ? (effectiveViewAsRole || user.role || 'admin') : (user?.role || 'rig');

  useEffect(() => {
    if (viewAsRole && !isAtLeast(user?.role, viewAsRole)) {
      setViewAsRoleState(null);
      safeLocalStorage.removeItem('drilllog-view-as-role');
    }
  }, [user?.role, viewAsRole]);

  const hydrateLocalUser = useCallback(async (cached) => {
    if (!cached?.id) return null;
    const localUser = await store('User').get(cached.id);
    const currentUser = localUser || cached;
    if (!localUser) await store('User').put(currentUser);
    setUser(currentUser);
    setIsAuthenticated(true);
    setAuthError(null);
    writeLocalAuth(currentUser);
    await persistAuthState(currentUser);
    return currentUser;
  }, []);

  const checkUserAuth = useCallback(async () => {
    setIsLoadingAuth(true);
    try {
      // Explicit logout is the ONLY local condition that clears auth.
      // Network loss, token refresh failure, or a missing remote session
      // must never turn an already-authenticated installation into a login.
      if (safeLocalStorage.getItem(EXPLICIT_LOGOUT_KEY) === '1') {
        setUser(null);
        setIsAuthenticated(false);
        setAuthError(null);
        return;
      }

      // FIRST: hydrate from local identity regardless of network state.
      // Do not use navigator.onLine here; WebViews can report stale network
      // status and Supabase's remote session must not gate offline startup.
      let cached = readLocalAuth();
      if (!cached?.id) {
        try {
          const persisted = await store('AuthState').get(AUTH_STATE_ID);
          if (persisted?.user?.id) {
            cached = persisted.user;
            writeLocalAuth(cached);
          }
        } catch (e) {
          console.warn('[DrillLogPro] persistent auth state read skipped:', e?.message || e);
        }
      }
      if (cached?.id) {
        const currentUser = await hydrateLocalUser(cached);

        // Cloud work is deliberately best-effort and happens after the app is
        // already authenticated. Failure here can never redirect to login.
        if (currentUser && authClient.configured) {
          try {
            const { data } = await authClient.getSession();
            const sessionUser = data?.session?.user;
            if (sessionUser?.id === currentUser.id) {
              try {
                // Login/resume only pulls the last published supervisor
                // snapshot. Local drilling changes remain queued until End
                // Shift or Force Sync.
                await cloud.syncFromCloud(sessionUser.id, store);
                const refreshedUser = await store('User').get(sessionUser.id);
                if (refreshedUser) {
                  writeLocalAuth(refreshedUser);
                  setUser(refreshedUser);
                }
              } catch (syncError) {
                console.warn('[DrillLogPro] initial cloud pull skipped:', syncError?.message || syncError);
              }
            }
          } catch (error) {
            console.warn('[DrillLogPro] cloud session check skipped:', error?.message || error);
          }
        }
        return;
      }

      // No local identity: this is a genuinely unauthenticated installation.
      // A cached Supabase session may still exist (e.g. after an upgrade), so
      // recover it locally without requiring a network request.
      if (authClient.configured) {
        try {
          const { data } = await authClient.getSession();
          const sessionUser = data?.session?.user;
          if (sessionUser?.id) {
            const localUser = await store('User').get(sessionUser.id);
            const currentUser = localUser || {
              id: sessionUser.id,
              auth_user_id: sessionUser.id,
              email: sessionUser.email || null,
              name: sessionUser.user_metadata?.name || sessionUser.email?.split('@')[0] || 'User',
              role: sessionUser.user_metadata?.role || 'rig',
            };
            if (!localUser) await store('User').put(currentUser);
            writeLocalAuth(currentUser);
            await persistAuthState(currentUser);
            setUser(currentUser);
            setIsAuthenticated(true);
            setAuthError(null);
            return;
          }
        } catch (error) {
          console.warn('[DrillLogPro] cached Supabase session read skipped:', error?.message || error);
        }
      }

      setUser(null);
      setIsAuthenticated(false);
      setAuthError(null);
    } catch (error) {
      // If a local identity exists, never convert a local storage/SQLite issue
      // into a false remote logout. Surface only real application errors.
      const cached = readLocalAuth();
      if (cached?.id) {
        try {
          await hydrateLocalUser(cached);
          return;
        } catch {}
      }
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(error?.status === 503 ? error : null);
    } finally {
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  }, [hydrateLocalUser]);

  useEffect(() => {
    let mounted = true;
    void checkUserAuth();

    if (!authClient.configured) {
      return () => { mounted = false; };
    }

    const { data } = authClient.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // An explicit logout is authoritative.
      if (event === 'SIGNED_OUT') {
        if (safeLocalStorage.getItem(EXPLICIT_LOGOUT_KEY) === '1') {
          clearLocalAuth();
          setUser(null);
          setIsAuthenticated(false);
          setAuthError(null);
        }
        // Ignore SIGNED_OUT otherwise. It can occur after connectivity loss
        // or a failed refresh and must not log an offline user out.
        setIsLoadingAuth(false);
        setAuthChecked(true);
        return;
      }

      if (!session?.user) {
        // INITIAL_SESSION with no remote session is not a logout. Preserve a
        // local authenticated identity if one exists.
        if (readLocalAuth()?.id) {
          setIsLoadingAuth(false);
          setAuthChecked(true);
          return;
        }
        setIsLoadingAuth(false);
        setAuthChecked(true);
        return;
      }

      // Remote session events can enrich/refresh the local identity, but they
      // are never required for continued offline access.
      try {
        const localUser = await store('User').get(session.user.id);
        const currentUser = localUser || {
          id: session.user.id,
          auth_user_id: session.user.id,
          email: session.user.email || null,
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
          role: session.user.user_metadata?.role || 'rig',
        };
        if (!localUser) await store('User').put(currentUser);
        if (!mounted) return;
        writeLocalAuth(currentUser);
        await persistAuthState(currentUser);
        setUser(currentUser);
        setIsAuthenticated(true);
        setAuthError(null);
      } catch (error) {
        if (!mounted) return;
        // Preserve an existing local identity on any remote callback failure.
        if (!readLocalAuth()?.id) {
          setUser(null);
          setIsAuthenticated(false);
          setAuthError(error?.status === 503 ? error : null);
        }
      } finally {
        if (mounted) {
          setIsLoadingAuth(false);
          setAuthChecked(true);
        }
      }
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [checkUserAuth]);

  useEffect(() => {
    if (!user?.id || !authChecked || !device?.startHeartbeat) return undefined;
    const stop = device.startHeartbeat(user.id, async () => {
      safeLocalStorage.setItem(EXPLICIT_LOGOUT_KEY, '1');
      clearLocalAuth();
      await clearPersistedAuthState();
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(new Error('This device was signed out by an administrator.'));
      try { if (authClient.configured) await authClient.signOut({ scope: 'local' }); } catch (_) { /* local forced logout remains authoritative */ }
      window.location.href = appPath('/login?forced=1');
    });
    return stop;
  }, [user?.id, authChecked]);

  const logout = async (shouldRedirect = true) => {
    // Mark local logout BEFORE touching Supabase so a SIGNED_OUT callback
    // cannot race with the local state transition.
    safeLocalStorage.setItem(EXPLICIT_LOGOUT_KEY, '1');
    clearLocalAuth();
    await clearPersistedAuthState();
    setUser(null);
    setIsAuthenticated(false);
    setAuthError(null);

    try {
      if (authClient.configured) {
        await authClient.signOut({ scope: 'global' });
      }
    } catch (error) {
      // Logging out locally must work even without network access.
      console.warn('[DrillLogPro] remote logout skipped:', error?.message || error);
    }

    if (shouldRedirect) window.location.href = appPath('/login');
  };

  const navigateToLogin = () => { window.location.href = appPath('/login'); };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      authChecked,
      logout,
      navigateToLogin,
      checkUserAuth,
      checkAppState: checkUserAuth,
      viewAsRole,
      setViewAsRole,
      effectiveRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
